// Storage health check for the field app (card #3995, 7 Oct update).
//
// Photos taken on an iPhone are sometimes lost because the phone cannot keep
// them: IndexedDB refuses or cannot read back a Blob, or the quota is nearly
// used. This module tells the tasker while they are still at the shelf, so
// they can keep the page open until the upload bar clears. It WARNS and never
// blocks: capture does not wait for it, and a failed check never stops a photo.
//
// Modelled on rt-client's StorageHealthService: a small test write with a
// read-back, plus navigator.storage.estimate(). It does not prove a photo will
// survive a suspension; it only makes a failing phone visible early.
//
// This file imports nothing so a test can load it on its own. The probe uses
// its own scratch database, never the app's tables, so no Dexie version bump
// and no risk to queued photos.

export const QUOTA_USED_WARN_PERCENT = 80
export const FREE_BYTES_WARN = 10 * 1024 * 1024
export const PROBE_BYTES = 64 * 1024
export const PROBE_TIMEOUT_MS = 5000

const PROBE_DB_NAME = "field_storage_probe"
const PROBE_STORE = "probe"
const PROBE_KEY = "photo"

export const REASON_QUOTA_HIGH   = "quota_high"
export const REASON_LOW_FREE     = "low_free"
export const REASON_PROBE_FAILED = "probe_failed"

function _probeBytes() {
  const bytes = new Uint8Array(PROBE_BYTES)
  for (let i = 0; i < bytes.length; i++) bytes[i] = (i * 31 + 7) % 251
  return bytes
}

function _request(request) {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror   = () => reject(request.error || Object.assign(new Error("IndexedDB request failed"), { name: "IDBRequestError" }))
  })
}

function _openProbeDb(idb) {
  return new Promise((resolve, reject) => {
    const request = idb.open(PROBE_DB_NAME, 1)
    request.onupgradeneeded = () => request.result.createObjectStore(PROBE_STORE)
    request.onsuccess       = () => resolve(request.result)
    request.onerror         = () => reject(request.error || Object.assign(new Error("IndexedDB open failed"), { name: "IDBOpenError" }))
    request.onblocked       = () => reject(Object.assign(new Error("IndexedDB open blocked"), { name: "IDBBlockedError" }))
  })
}

function _withTimeout(promise, timeoutMs) {
  let timer
  const timeout = new Promise((_, reject) => {
    timer = setTimeout(() => reject(Object.assign(new Error("Storage probe timed out"), { name: "ProbeTimeout" })), timeoutMs)
  })
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timer))
}

// Writes a small Blob, reads it back and compares every byte. Resolves when
// the stored copy is identical, rejects with the browser's own error otherwise
// (UnknownError, NotFoundError, QuotaExceededError...) or ReadBackMismatch.
async function _probeWriteRead(idb) {
  const expected = _probeBytes()
  const db = await _openProbeDb(idb)
  try {
    const writeTx = db.transaction(PROBE_STORE, "readwrite")
    const written = _request(writeTx.objectStore(PROBE_STORE).put(new Blob([expected], { type: "application/octet-stream" }), PROBE_KEY))
    const done    = new Promise((resolve, reject) => {
      writeTx.oncomplete = () => resolve()
      writeTx.onerror    = () => reject(writeTx.error)
      writeTx.onabort    = () => reject(writeTx.error || Object.assign(new Error("Probe write aborted"), { name: "AbortError" }))
    })
    await Promise.all([written, done])

    const stored = await _request(db.transaction(PROBE_STORE, "readonly").objectStore(PROBE_STORE).get(PROBE_KEY))
    if (!(stored instanceof Blob)) {
      throw Object.assign(new Error("Probe read back no Blob"), { name: "ReadBackMismatch" })
    }
    const actual = new Uint8Array(await stored.arrayBuffer())
    const same = actual.length === expected.length && actual.every((byte, i) => byte === expected[i])
    if (!same) {
      throw Object.assign(new Error("Probe bytes differ after read-back"), { name: "ReadBackMismatch" })
    }

    await _request(db.transaction(PROBE_STORE, "readwrite").objectStore(PROBE_STORE).delete(PROBE_KEY)).catch(() => {})
  } finally {
    db.close()
  }
}

async function _estimate(storage) {
  try {
    const estimate = await storage?.estimate?.()
    if (!estimate || !(estimate.quota > 0)) return { quotaBytes: null, usageBytes: null, freeBytes: null, usedPercent: null }
    const usage = estimate.usage || 0
    return {
      quotaBytes:  estimate.quota,
      usageBytes:  usage,
      freeBytes:   Math.max(0, estimate.quota - usage),
      usedPercent: Math.round((usage / estimate.quota) * 1000) / 10
    }
  } catch {
    return { quotaBytes: null, usageBytes: null, freeBytes: null, usedPercent: null }
  }
}

async function _persisted(storage) {
  try {
    const result = await storage?.persisted?.()
    return typeof result === "boolean" ? result : null
  } catch {
    return null
  }
}

// Lightweight reading: navigator.storage.estimate() plus the persisted flag,
// no write/read probe (card #4215). Distinct from checkStorageHealth below,
// which actually writes a test Blob for the degraded-banner warning (card
// #3995) -- that probe is too heavy to run on every app open, shift start and
// vault failure, so the periodic "Storage reading" telemetry uses this
// instead. Never throws.
export async function estimateStorage({
  storage = typeof navigator === "undefined" ? null : navigator.storage
} = {}) {
  const estimate = await _estimate(storage)
  return { ...estimate, persisted: await _persisted(storage) }
}

// One health check. Never throws. Resolves:
//   { degraded, reasons, probeOk, errorName, probeTimedOut, quotaBytes, usageBytes, freeBytes, usedPercent }
// degraded is true when the probe failed, more than 80 percent of the quota is
// used, or less than 10 MB is free. Unknown quota is not a warning.
//
// Card #4088 (support card #4087, Therese Simmonds) -- a bare probe timeout is
// not proof the phone can't save photos: Jay saw this fire on a healthy phone
// (41 GB quota, 1.2% used, every upload a 201) purely because the 5s
// write/read-back probe didn't land in time. A real write failure, read-back
// mismatch or low-quota reading throws or resolves on the first try and is
// never retried here; only a timeout (ProbeTimeout) gets a second attempt
// before counting against the phone. probeTimedOut is true whenever the first
// attempt timed out, whether or not the retry then succeeded, so the caller
// can log the timeout's rate even when it never becomes a warning.
export async function checkStorageHealth({
  storage = typeof navigator === "undefined" ? null : navigator.storage,
  idb = typeof indexedDB === "undefined" ? null : indexedDB,
  timeoutMs = PROBE_TIMEOUT_MS
} = {}) {
  const reasons = []
  let errorName = null
  let probeTimedOut = false

  const estimate = await _estimate(storage)
  if (estimate.usedPercent !== null && estimate.usedPercent > QUOTA_USED_WARN_PERCENT) reasons.push(REASON_QUOTA_HIGH)
  if (estimate.freeBytes !== null && estimate.freeBytes < FREE_BYTES_WARN) reasons.push(REASON_LOW_FREE)

  try {
    if (!idb) throw Object.assign(new Error("IndexedDB unavailable"), { name: "IDBUnavailable" })
    await _withTimeout(_probeWriteRead(idb), timeoutMs)
  } catch (error) {
    if (error?.name === "ProbeTimeout") {
      probeTimedOut = true
      try {
        await _withTimeout(_probeWriteRead(idb), timeoutMs)
      } catch (retryError) {
        errorName = retryError?.name || "Error"
        reasons.push(REASON_PROBE_FAILED)
      }
    } else {
      errorName = error?.name || "Error"
      reasons.push(REASON_PROBE_FAILED)
    }
  }

  return { degraded: reasons.length > 0, reasons, probeOk: errorName === null, errorName, probeTimedOut, ...estimate }
}
